import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-scroll";
import { useNavigate } from "react-router-dom";
import { FaLinkedin, FaGithub, FaInstagram, FaFacebook, FaHeart, FaEnvelope, FaPhone, FaMapMarkerAlt, FaArrowRight } from "react-icons/fa";

const quickLinks = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Refund Policy", path: "/refund-policy" },
  { label: "Terms & Conditions", path: "/terms-and-conditions" },
  { label: "Legal Policy", path: "/legal-policy" },
];

const footerLinks = {
  Services: [
    { label: "Web Development", to: "services" },
    { label: "Mobile App Development", to: "services" },
    { label: "Digital Marketing", to: "services" },
    { label: "Business Strategy", to: "services" },
    { label: "Domain & Hosting", to: "services" },
    { label: "Social Media Management", to: "services" },
  ],
  Company: [
    { label: "About Us", to: "about" },
    { label: "Our Team", to: "team" },
    { label: "Portfolio", to: "portfolio" },
    { label: "Testimonials", to: "testimonials" },
    { label: "Contact", to: "contact" },
  ],
  Support: [
    { label: "24/7 Customer Support", to: "contact" },
    { label: "Get Free Quote", to: "contact" },
    { label: "Technical Support", to: "contact" },
    { label: "Project Consultation", to: "contact" },
    { label: "Business Analysis", to: "contact" },
  ],
};

const socials = [
  { icon: <FaLinkedin />, href: "https://linkedin.com", label: "LinkedIn", color: "#0077B5" },
  { icon: <FaInstagram />, href: "https://www.instagram.com/onexvorabusiness?igsh=MXh4Y2JpdXpjYzRscg==", label: "Instagram", color: "#E4405F" },
  { icon: <FaFacebook />, href: "https://www.facebook.com/share/1DZfFNEvQd/", label: "Facebook", color: "#1877F2" },
  { icon: <FaGithub />, href: "https://github.com/onexvorabusinesssolutions-coder", label: "GitHub", color: "#333" },
];

export default function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="relative pt-20 pb-8 overflow-hidden bg-gradient-to-b from-darker to-black">
      <div className="section-divider mb-0" />
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/10 to-transparent" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12 pt-12">
          {/* Brand Section - Enhanced */}
          <div className="lg:col-span-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="relative">
                  <img src="/circle logo.jpeg" alt="ONexvora" className="h-12 w-12 rounded-full object-contain" />
                  <div className="absolute inset-0 rounded-full bg-blue-500/20 blur-sm" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-xl">Onexvora</h3>
                  <p className="text-blue-400 text-sm font-medium">Business Solutions</p>
                </div>
              </div>
              
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                Transforming businesses through innovative digital solutions. We combine cutting-edge technology with strategic thinking to deliver exceptional results that drive growth and success.
              </p>
            </motion.div>
            
            {/* Contact Info - Enhanced */}
            <div className="space-y-3 mb-6">
              <motion.a 
                href="https://maps.google.com/?q=D-184,+Sector-63,+Noida,+UP+-+201301,+India"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: 5 }}
                className="flex items-center gap-3 text-gray-300 hover:text-blue-400 transition-all duration-300 group cursor-pointer"
              >
                <div className="w-10 h-10 glass rounded-full flex items-center justify-center group-hover:bg-blue-500/20">
                  <FaMapMarkerAlt className="text-blue-400 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-medium">D-184, Sector-63, Noida, UP - 201301</p>
                  <p className="text-xs text-gray-400">Uttar Pradesh, India • Click to view map</p>
                </div>
              </motion.a>
              
              <motion.a 
                href="mailto:onexvorabusinesssolutions@gmail.com"
                whileHover={{ x: 5 }}
                className="flex items-center gap-3 text-gray-300 hover:text-blue-400 transition-all duration-300 group"
              >
                <div className="w-10 h-10 glass rounded-full flex items-center justify-center group-hover:bg-blue-500/20">
                  <FaEnvelope className="text-blue-400 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-medium">onexvorabusinesssolutions@gmail.com</p>
                  <p className="text-xs text-gray-400">Business Inquiries</p>
                </div>
              </motion.a>
              
              <motion.a 
                href="tel:+919718094931"
                whileHover={{ x: 5 }}
                className="flex items-center gap-3 text-gray-300 hover:text-blue-400 transition-all duration-300 group"
              >
                <div className="w-10 h-10 glass rounded-full flex items-center justify-center group-hover:bg-blue-500/20">
                  <FaPhone className="text-blue-400 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-medium">+91 97180 94931</p>
                  <p className="text-sm font-medium">+91 99909 96731</p>
                  <p className="text-xs text-gray-400">24/7 Support</p>
                </div>
              </motion.a>
            </div>
            
            {/* Social Media - Enhanced */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">Follow Us</h4>
              <div className="flex gap-3">
                {socials.map((social, i) => (
                  <motion.a 
                    key={social.label}
                    href={social.href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ scale: 1.1, y: -3 }}
                    className="w-11 h-11 glass rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 group relative overflow-hidden"
                    aria-label={social.label}
                  >
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-full"
                      style={{ background: social.color }}
                    />
                    <span className="relative z-10">{social.icon}</span>
                  </motion.a>
                ))}
              </div>
            </div>
          </div>

          {/* Links Sections - Enhanced */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {Object.entries(footerLinks).map(([title, links], sectionIndex) => (
                <motion.div 
                  key={title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: sectionIndex * 0.1 }}
                >
                  <h4 className="text-white font-bold mb-6 text-base relative">
                    {title}
                    <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" />
                  </h4>
                  <ul className="space-y-3">
                    {links.map((link, i) => (
                      <motion.li 
                        key={link.label}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: (sectionIndex * 0.1) + (i * 0.05) }}
                      >
                        <Link 
                          to={link.to} 
                          smooth 
                          duration={600} 
                          offset={-80}
                          className="text-gray-400 hover:text-blue-400 text-sm transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group cursor-pointer"
                        >
                          <FaArrowRight className="text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
                          {link.label}
                        </Link>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Newsletter Section - Enhanced */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass rounded-3xl p-8 mb-12 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10" />
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-center lg:text-left">
              <h3 className="text-white font-bold text-xl lg:text-2xl mb-2">Stay Ahead of the Curve</h3>
              <p className="text-gray-300 text-sm lg:text-base">Get the latest insights on technology trends, business strategies, and digital transformation.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <input 
                type="email" 
                placeholder="Enter your professional email"
                className="flex-1 lg:w-80 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-white/15 transition-all text-sm backdrop-blur-sm" 
              />
              <motion.button 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="btn-primary px-8 py-3 rounded-xl text-white font-semibold text-sm whitespace-nowrap flex items-center gap-2"
              >
                <span>Subscribe</span>
                <FaArrowRight className="text-xs" />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Bottom Section - Enhanced */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 text-sm">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <p className="text-gray-300">© 2025 Onexvora Business Solutions. All rights reserved.</p>
              <div className="flex items-center gap-2 text-gray-400">
                <span>Made with</span>
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <FaHeart className="text-red-500" />
                </motion.div>
                <span>by Onexvora Team</span>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6">
              {quickLinks.map((link) => (
                <button 
                  key={link.label} 
                  onClick={() => navigate(link.path)}
                  className="text-gray-400 hover:text-blue-400 transition-colors text-xs hover:underline"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}